/* -------------------------------------------------------------------------- */
/*                                DATADOG SETUP                               */
/* -------------------------------------------------------------------------- */

import { datadogRum, RumEvent } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { getAnonymousUuid } from 'utils/auth';
import { env } from '../../constants';

/* -------------------------------------------------------------------------- */
/*                                   CONFIG                                   */
/* -------------------------------------------------------------------------- */

const applicationId = env.VITE_DATADOG_APPLICATION_ID;
const clientToken = env.VITE_DATADOG_CLIENT_TOKEN;

export const allowedTracingUrls =
  env.VITE_ENV === 'production'
    ? [
        /^https:\/\/pro-core\.zenjob\.com/,
        /^https:\/\/zj-live\.live\.zenjob\.com/
      ]
    : [
        /^https:\/\/staging-pro-core\.zenjob\.org/,
        /^https:\/\/zj-staging\.preprod\.zenjob\.org/
      ];

/* -------------------------------------------------------------------------- */
/*                             INTERNAL STATE                                 */
/* -------------------------------------------------------------------------- */

let isInitialized = false;

/**
 * Remove the guest magic-link token (`?t=`) from a URL before it is recorded by RUM.
 * The token rides in the `/guest/curriculumVitaes?t=…` entry URL; without this it would be captured
 * verbatim in RUM `view.url` / session replay. Returns the URL unchanged if unparseable.
 */
export const scrubLinkToken = (url: string | undefined): string | undefined => {
  if (!url) {
    return url;
  }
  try {
    const parsed = new URL(url);
    if (parsed.searchParams.has('t')) {
      parsed.searchParams.delete('t');
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

/** RUM `beforeSend`: strip the guest magic-link token from a view's URL + referrer. */
export const scrubRumEvent = (event: RumEvent): void => {
  if (event.view) {
    event.view.url = scrubLinkToken(event.view.url) ?? event.view.url;
    event.view.referrer =
      scrubLinkToken(event.view.referrer) ?? event.view.referrer;
  }
};

/**
 * Expected guest-CV grant outcomes: link reopened after submitting, 7-day grant window
 * over, 60-min session lapse. The app routes all of these to friendly screens and counts
 * them as RUM actions (`guest_cv_grant_outcome`) — but react-query v3 also console.errors
 * every failed mutation (core/mutation.js → getLogger().error), which RUM re-captures as
 * a console-source error. Drop them at the door. Deliberately narrow:
 * `error.access-grant.invalid` and unknown codes MUST keep reaching Error Tracking.
 */
const EXPECTED_GUEST_OUTCOME_PATTERN =
  /error\.access-grant\.(already-submitted|expired)|error\.guest\.session-expired/;

export const isExpectedGuestOutcomeError = (event: RumEvent): boolean =>
  event.type === 'error' &&
  EXPECTED_GUEST_OUTCOME_PATTERN.test(event.error.message);

/**
 * Crawlers execute our SPA JavaScript and would otherwise create RUM sessions — and
 * errors (e.g. their sandboxed runtime rejecting service-worker registration) — that
 * pollute Error Tracking and skew metrics. Skip RUM entirely for known bots.
 */
const BOT_UA_PATTERN =
  /bot|crawler|spider|crawling|Google-Read-Aloud|AdsBot|Googlebot|Chrome-Lighthouse|HeadlessChrome/i;

export const isLikelyBot = (): boolean =>
  typeof navigator !== 'undefined' && BOT_UA_PATTERN.test(navigator.userAgent);

/* -------------------------------------------------------------------------- */
/*                                ENTRY POINT                                 */
/* -------------------------------------------------------------------------- */

export const initDatadog = (): void => {
  if (
    !env.PROD ||
    !applicationId ||
    !clientToken ||
    env.VITE_ENV === 'development' ||
    isLikelyBot() ||
    isInitialized
  ) {
    return;
  }

  const userId = getAnonymousUuid();

  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.eu',
    service: 'pro-talent-app',
    env: env.VITE_ENV,
    // Release version (git SHA, injected at build). Ties every error/session to a deploy —
    // essential for attributing regressions (e.g. stale-chunk failures after a release) and
    // for matching uploaded source maps. Omitted (undefined) in local/dev builds.
    version: env.VITE_APP_VERSION,
    sessionSampleRate: 50,
    sessionReplaySampleRate: 25,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls,
    plugins: [reactPlugin({ router: false })],
    beforeSend: event => {
      // Never let the guest magic-link token reach RUM (view URLs / replay).
      scrubRumEvent(event);
      // Expected guest outcomes are actions, not errors — drop the error-shaped copies.
      if (isExpectedGuestOutcomeError(event)) {
        return false;
      }
      return true;
    }
  });
  datadogRum.setUser({ id: userId });
  isInitialized = true;
};

export const datadogLogError = (
  message: string,
  details?: unknown,
  // Structured context attached to the RUM error so it is queryable/groupable in Error Tracking
  // (e.g. { errorCode, step, feature }) instead of only living inside the free-text message.
  context?: Record<string, unknown>
): void => {
  if (!isInitialized) return;
  if (details instanceof Error) {
    datadogRum.addError(details, { message, ...context });
  } else {
    datadogRum.addError(message, { details, ...context });
  }
};

export const datadogTrackEvent = (
  name: string,
  context?: Record<string, unknown>
): void => {
  if (!isInitialized) return;
  datadogRum.addAction(name, context);
};

export const logError = (
  message: string,
  details?: unknown,
  context?: Record<string, unknown>
): void => {
  // When RUM is initialized it auto-captures console.error as a separate
  // (console-source) error, double-counting alongside the addError below — so
  // suppress the console line there. When RUM is NOT initialized (dev, or a prod
  // build without a client token) addError is a no-op, so keep the console output
  // to avoid silently dropping the error.
  if (!isInitialized) {
    if (details === undefined) {
      console.error(message);
    } else {
      console.error(message, details);
    }
  }
  datadogLogError(message, details, context);
};
